import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Check } from "lucide-react";
import usePageMeta from "@/hooks/usePageMeta";

// Route A · "Lekker Leven"
// Palette: koraal #E06B3F, grasgroen #78A85A, geel #F0B98D, crème #FFF6E8
// Motif: organische zon-curve / route-lijn
// Photos: dóende mensen — koken, fietsen, bakken, wandelen

const PALETTE = {
  bg: "#FFF6E8",
  ink: "#1F2A22",
  koraal: "#E06B3F",
  grasgroen: "#78A85A",
  geel: "#F0B98D",
  surface: "#FBEBC8",
};

const IMGS = {
  koken: "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  fietsen: "https://images.unsplash.com/photo-1484627147104-f5197bcd6651?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
  brood: "https://images.unsplash.com/photo-1509440159596-0249088772ff?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  wandelen: "https://images.unsplash.com/photo-1723227123897-0624fe046ae8?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  tuin: "https://images.unsplash.com/photo-1416879595882-3373a0480b5b?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
};

const ease = [0.22, 1, 0.36, 1];

function CurveMotif({ color = PALETTE.koraal, className = "" }) {
  return (
    <svg className={className} viewBox="0 0 800 200" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <motion.path
        d="M 10 150 C 120 20, 260 200, 400 100 S 700 20, 790 120"
        stroke={color}
        strokeWidth="4"
        strokeLinecap="round"
        initial={{ pathLength: 0 }}
        whileInView={{ pathLength: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 2, ease }}
      />
      <motion.circle
        cx="790"
        cy="120"
        r="6"
        fill={color}
        initial={{ scale: 0 }}
        whileInView={{ scale: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.4, delay: 1.7 }}
      />
    </svg>
  );
}

function HeroDeelnemer() {
  return (
    <section className="relative overflow-hidden" style={{ background: PALETTE.bg, color: PALETTE.ink }}>
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 pt-16 pb-24 relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <p className="text-xs uppercase tracking-[0.35em]" style={{ color: PALETTE.koraal }}>
              Voor jou · Deelnemer
            </p>
            <h1 className="font-heading font-light text-[44px] sm:text-6xl lg:text-[88px] leading-[0.98] tracking-tight mt-8">
              Het leven mag hier
              <br />
              <span className="italic" style={{ color: PALETTE.koraal }}>gewoon weer leuk</span> zijn.
            </h1>
            <p className="mt-8 max-w-xl text-xl leading-relaxed" style={{ color: "#3d4a3e" }}>
              Waar zou jij weer zin in willen krijgen? Fietsen, koken, iets maken, iemand ontmoeten — één vonk is genoeg om te beginnen.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              {["Startpunt", "Rugzak", "Vonk", "Volgende level"].map((w) => (
                <span key={w} className="text-sm px-4 py-2 rounded-full border" style={{ borderColor: PALETTE.grasgroen, color: PALETTE.grasgroen }}>
                  {w}
                </span>
              ))}
            </div>
            <div className="mt-12 flex flex-col sm:flex-row gap-3">
              <button
                className="group inline-flex items-center gap-4 rounded-full pl-7 pr-2 py-2 transition-all"
                style={{ background: PALETTE.ink, color: PALETTE.bg }}
              >
                <span className="text-base font-medium">Doe de zelfscan</span>
                <span className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: PALETTE.koraal }}>
                  <ArrowUpRight size={18} color="white" />
                </span>
              </button>
              <button className="text-base underline underline-offset-4" style={{ color: PALETTE.ink }}>
                Lees een verhaal
              </button>
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="grid grid-cols-2 gap-3">
              <div className="aspect-[3/4] rounded-3xl overflow-hidden col-span-2">
                <img src={IMGS.koken} alt="Samen koken" className="w-full h-full object-cover" />
              </div>
              <div className="aspect-square rounded-2xl overflow-hidden">
                <img src={IMGS.brood} alt="Brood maken" className="w-full h-full object-cover" />
              </div>
              <div className="aspect-square rounded-2xl overflow-hidden">
                <img src={IMGS.fietsen} alt="Fietsen" className="w-full h-full object-cover" />
              </div>
            </div>
            {/* Floating sticker */}
            <motion.div
              initial={{ rotate: -8, opacity: 0, y: 20 }}
              animate={{ rotate: -8, opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6, ease }}
              className="absolute -top-6 -right-4 px-4 py-3 font-heading italic text-lg rounded-full shadow-lg"
              style={{ background: PALETTE.geel, color: PALETTE.ink }}
            >
              Eén vonk is genoeg
            </motion.div>
          </div>
        </div>

        <div className="mt-24">
          <CurveMotif className="w-full h-24" />
        </div>
      </div>
    </section>
  );
}

function HeroOpdrachtgever() {
  return (
    <section className="relative" style={{ background: PALETTE.surface, color: PALETTE.ink }}>
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-6">
            <p className="text-xs uppercase tracking-[0.35em]" style={{ color: PALETTE.grasgroen }}>
              Voor u · Gemeente & Onderwijs
            </p>
            <h2 className="font-heading font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight mt-6">
              Meetbaar meedoen
              <br />
              begint met <span className="italic" style={{ color: PALETTE.koraal }}>plezier.</span>
            </h2>
            <p className="mt-6 max-w-xl text-lg leading-relaxed" style={{ color: "#3d4a3e" }}>
              Een voorbereidende ontwikkelfase van twaalf maanden — vóór regulier traject. Zichtbare beweging op belastbaarheid, structuur en eigenaarschap.
            </p>
            <ul className="mt-8 space-y-3 max-w-lg">
              {["Nul-, tussen- en eindmetingen", "Warm overdragen naar participatie of onderwijs", "Rapportage voor beleid én bestuur"].map((t) => (
                <li key={t} className="flex gap-3 items-start">
                  <span className="mt-1.5 w-2 h-2 rounded-full shrink-0" style={{ background: PALETTE.koraal }} />
                  <span className="text-base">{t}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="rounded-3xl p-6 border" style={{ background: PALETTE.bg, borderColor: PALETTE.grasgroen + "40" }}>
                <p className="text-xs uppercase tracking-[0.25em]" style={{ color: PALETTE.grasgroen }}>Cohort 1 · Q2</p>
                <p className="font-heading text-5xl mt-3">12</p>
                <p className="text-sm mt-2 opacity-80">deelnemers · Rotterdam Zuid</p>
              </div>
              <div className="rounded-3xl p-6 border" style={{ background: PALETTE.geel + "40", borderColor: PALETTE.geel }}>
                <p className="text-xs uppercase tracking-[0.25em]" style={{ color: PALETTE.ink }}>Nulmeting → 6 mnd</p>
                <p className="font-heading text-5xl mt-3">+34%</p>
                <p className="text-sm mt-2 opacity-80">belastbaarheid</p>
              </div>
              <div className="rounded-3xl overflow-hidden aspect-square col-span-2">
                <img src={IMGS.tuin} alt="Tuinieren samen" className="w-full h-full object-cover" />
              </div>
            </div>
            <div className="mt-6 flex items-center gap-4">
              <button
                className="group inline-flex items-center gap-4 rounded-full pl-7 pr-2 py-2 transition-all"
                style={{ background: PALETTE.ink, color: PALETTE.bg }}
              >
                <span className="text-base font-medium">Plan pilot-gesprek</span>
                <span className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: PALETTE.grasgroen }}>
                  <ArrowUpRight size={18} color="white" />
                </span>
              </button>
              <span className="text-sm italic opacity-70">Business case in 15 minuten</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function MoodboardA() {
  const [tab, setTab] = useState("deelnemer");
  usePageMeta({ title: "Route A · Lekker Leven · Moodboard" });

  return (
    <div data-testid="moodboard-a" style={{ background: PALETTE.bg }}>
      <Toolbar current="a" tab={tab} setTab={setTab} palette={PALETTE} />
      {tab === "deelnemer" ? <HeroDeelnemer /> : <HeroOpdrachtgever />}
      <PaletteFooter palette={PALETTE} name="Lekker Leven" font='"Cormorant Garamond" + "Manrope"' />
    </div>
  );
}

export function Toolbar({ current, tab, setTab, palette }) {
  return (
    <div className="sticky top-0 z-50 backdrop-blur-md border-b" style={{ background: palette.bg + "e6", borderColor: palette.ink + "15" }}>
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 h-16 flex items-center justify-between">
        <Link to="/moodboard" className="text-xs uppercase tracking-[0.3em]" style={{ color: palette.ink }} data-testid="moodboard-back">
          ← Alle routes
        </Link>
        <div className="flex gap-1 p-1 rounded-full border" style={{ borderColor: palette.ink + "25" }}>
          {["deelnemer", "opdrachtgever"].map((t) => (
            <button
              key={t}
              data-testid={`moodboard-tab-${t}`}
              onClick={() => setTab(t)}
              className="text-xs uppercase tracking-[0.25em] px-4 py-2 rounded-full transition-colors"
              style={{
                background: tab === t ? palette.ink : "transparent",
                color: tab === t ? palette.bg : palette.ink,
              }}
            >
              {t === "deelnemer" ? "Deelnemer" : "Opdrachtgever"}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          {["a", "b", "c"].map((r) => (
            <Link
              key={r}
              to={`/moodboard/${r}`}
              className="w-8 h-8 rounded-full text-xs flex items-center justify-center border font-medium"
              style={{
                borderColor: palette.ink + "30",
                background: current === r ? palette.ink : "transparent",
                color: current === r ? palette.bg : palette.ink,
              }}
            >
              {r.toUpperCase()}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

export function PaletteFooter({ palette, name, font }) {
  const chips = Object.entries(palette).filter(([k]) => k !== "surface");
  return (
    <section className="py-16 border-t" style={{ background: palette.bg, borderColor: palette.ink + "15", color: palette.ink }}>
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 grid grid-cols-1 md:grid-cols-2 gap-10">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] opacity-60">Route · {name}</p>
          <h3 className="font-heading text-3xl mt-2">Palet & typografie</h3>
          <p className="text-sm mt-4 opacity-75">Font stack: {font}</p>
        </div>
        <div className="grid grid-cols-4 gap-3">
          {chips.map(([k, v]) => (
            <div key={k}>
              <div className="aspect-square rounded-2xl" style={{ background: v, border: `1px solid ${palette.ink}20` }} />
              <p className="text-xs mt-2 uppercase tracking-wider opacity-70">{k}</p>
              <p className="text-xs opacity-50">{v}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
